import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { RowFillerTransform, RowFillerTransformConfig, RowMissesOption } from 'src/app/domainObjects/transformers/rowFillerTransform';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { ConfigService } from 'src/app/services/config/config.service';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformerViewModel } from '../../../transformer-list/transformer-list.component';
import { RowFillerRuleViewModel } from './rowFillerRuleViewModel';
import { TransformDetailViewModelBase } from '../../../dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';
import { DataDictionaryService } from 'src/app/services/dataDictionary/data-dictionary.service';
import { ValidationInfo } from 'src/app/objectModel/validationInfo';
import { RequiredFieldValidation } from '../../../../objectModel/validationInfo';
import { TransformerParametersViewModel } from '../transformer-parameters/transformerParametersViewModel';
import { IncludeInfo } from 'src/app/domainObjects/includeInfo';
import { RowFillerPreviewDataResponse } from 'src/app/messaging/rowFillerPreviewDataResponse';
import { TransformType } from 'src/app/domainObjects/transformType';

export class RowFillerViewModel extends TransformDetailViewModelBase<RowFillerTransform> {
    farmType: DataFarmType;
    groupBy: string;
    defCurrentRow: string;
    userColumn: string;
    timeColumn: string;
    rowMissesOption:RowMissesOption;
    missName: string;
    runAll: boolean;
    
    separateBy: string;
    defTimeout: number;
    // advanced settings
    bufferRows: number;
    threads: number;
    transRows: number;

    rules: Array<RowFillerRuleViewModel> = [];
    // Preview options
    rows: number;
    sampleStart: number;
    sampleEnd: number;
    // Status
    groupHitCount?: number;
    hitCount?: number;
    missCount?: number;
    hitRatio?: number;
    hitRatioDisplay?: string;
  
    request: PreviewDataImportRequest;
    response: RowFillerPreviewDataResponse;
  
    constructor(
      dataDictService: DataDictionaryService,
      private configService: ConfigService,
      configViewModel: DataTransformConfigViewModel,
      transformViewModel: TransformerViewModel) {

      super(dataDictService, configViewModel, transformViewModel);

      this.transform = transformViewModel.transform;
      const transformConfig = this.transform.transformConfig;
      this.parameters=new TransformerParametersViewModel();
      if (transformConfig) {
          this.farmType = transformConfig.farmType ?? transformViewModel.transform.dataFarmType;
          this.userColumn = transformConfig.userField;
          this.sampleEnd = transformConfig.sampleEnd + 1;
          this.sampleStart = transformConfig.sampleStart + 1;
          this.timeColumn = transformConfig.timestampField;
          this.runAll = transformConfig.runAll;
          this.missName = transformConfig.missesNamed;
          this.sortInfo = transformConfig.sortInfo;
          if(transformConfig.includeInResults){
            this.includeInResults = transformConfig.includeInResults.map(item =>new IncludeInfo(item));
            this.parameters.includeInResults=this.includeInResults;
          }
          this.defTimeout = transformConfig.defTimeout;
          this.rowMissesOption = transformConfig.rowMissesOption;
          this.separateBy = transformConfig.separateBy;
          //parameters
          // this.parameters.sourceColumn= transformConfig.defSourceField;
          // this.parameters.targetColumn= transformConfig.defTargetField;
          this.parameters.userColumn= transformConfig.userField;
          this.parameters.timeColumn= transformConfig.timestampField;
          this.parameters.sortInfo= transformConfig.sortInfo;
          //this.parameters.includeInResults= transformConfig.in;
          this.parameters.sampleStart = transformConfig.sampleStart+ 1;
          this.parameters.sampleEnd = transformConfig.sampleEnd+ 1;
          this.parameters.runAll= transformConfig.runAll;
          //this.parameters.deleteMisses= transformConfig.deleteMisses;
          this.parameters.missName= transformConfig.missesNamed;
          // advanced settings
          this.bufferRows = transformConfig.bufferRows;
          this.threads = transformConfig.threads;
          this.transRows = transformConfig.transRows;
          this.groupBy = transformConfig.groupBy;
          this.defCurrentRow = transformConfig.defCurrentRow;
          
          if (transformConfig.rules) {
            this.rules = transformConfig.rules.map(r => new RowFillerRuleViewModel(r));
          }

          // default
          if (!this.timeColumn || this.timeColumn?.length === 0) {
            this.timeColumn = this.getDefaultTimestampColumn();
          }
        }
    }
    
    syncSeparateByWithPrimarySort(): void {
      if (this.separateBy) {
        if (!this.sortInfo?.find(x => x.sortField === this.separateBy)) {
          this.separateBy = null;
        }
      }
    }
  
    override configureValidationInfo(): ValidationInfo[] {
      const infos = [];

      infos.push( new RequiredFieldValidation('groupByField', 'Group By'));
      infos.push( new RequiredFieldValidation('userField', 'User Field'));
      infos.push( new RequiredFieldValidation('timestampField', 'Timestamp Field'));
      infos.push( new RequiredFieldValidation('separateBy', 'Separate By'));
      
      return infos;
    }

    override onTransformRunCompleted(response: RowFillerPreviewDataResponse): void {
      // A cache-hit run carries no stats at all (they're deliberately stripped
      // before a CachedProcessingResult is persisted -- see
      // project_stats_vs_fullrun_separation memory). Nothing has changed since
      // whatever last populated the stats currently on screen, so they're still
      // accurate -- leave them exactly as they are instead of wiping them blank.
      if (
        !response?.previewStats ||
        !response?.totalStats ||
        !response.totalStats.globalStat ||
        !response.totalStats.ruleStats ||
        !response.previewStats.globalStat ||
        !response.previewStats.ruleStats
      ) {
        return;
      }

      // Clear all stats prior to rebinding new values
      this.groupHitCount = null;
      this.hitCount = null;
      this.hitRatio = null;
      this.missCount = null;
      this.hitRatioDisplay ="0.00%";
      this.rows = 0;
      this.rules.forEach( r => {
        r.groupHits = null;
        r.rowHits = null;
        r.sampleGroupHits = null;
        r.sampleRowHits = null;
      });

      if (this.runAll) {
        // Map Transform Global and Rule stats
        this.groupHitCount = response.totalStats?.globalStat.groupHits;
        this.hitCount = response.totalStats?.globalStat.rowHits;

        if (response.totalStats?.globalStat.hitRate) {
          this.hitRatio = response.totalStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }
        this.missCount = response.totalStats?.globalStat.rowMisses;
      } else {
        // Map Transform Global and Rule stats
        this.groupHitCount = response.previewStats?.globalStat.groupHits;
        this.hitCount = response.previewStats.globalStat.rowHits;

        if (response.previewStats.globalStat.hitRate) {
          this.hitRatio = response.previewStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }
        this.missCount = response.previewStats.globalStat.rowMisses;
      }
      
      this.rows = this.runAll ? response.currentData?.totalRows : response.activeData?.totalRows;
      // map the rule stats to the rule
      if (response.previewStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.previewStats.ruleStats.find(r => r.ruleIndex === i);
          this.rules[i].sampleGroupHits = ruleStat?.groupHits;
          this.rules[i].sampleRowHits = ruleStat?.rowHits;
        }
      }
      // map the rule stats to the rule
      if (response.totalStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.totalStats.ruleStats.find(r => r.ruleIndex === i);
          this.rules[i].groupHits = ruleStat?.groupHits;
          this.rules[i].rowHits = ruleStat?.rowHits;
        }
      }
    }

    reindexRules(): void {
      for (let i = 0; i < this.rules.length; i++) {
        this.rules[i].index = i + 1;
      }
      this.rules.sort((a, b) => a.index - b.index);
    }

    override toTransformConfig(): RowFillerTransformConfig {
      const config = new RowFillerTransformConfig();
      config.farmType = this.farmType;
      config.transformType = TransformType.rowFiller;
      config.groupBy = this.groupBy;
      config.defCurrentRow = this.defCurrentRow;
      config.userField=this.parameters.userColumn;
      config.timestampField=this.parameters.timeColumn;
      if(this.parameters.sortInfo){
        if(this.parameters.sortInfo.length==0){
          config.sortInfo =undefined;
        }else{
          config.sortInfo=this.parameters.sortInfo;
        }
      }
      if(this.parameters.includeInResults){
        if(this.parameters.includeInResults.length==0){
          config.includeInResults =undefined;
        }else{
          config.includeInResults =this.parameters.includeInResults.map(item => item.includeField);
        }
      }
      config.sampleStart =this.parameters.sampleStart-1;
      config.sampleEnd =this.parameters.sampleEnd-1;
      config.rowMissesOption =this.rowMissesOption;
      config.missesNamed =this.missName;
      config.runAll=this.runAll;
      
      config.separateBy = this.separateBy;
      config.defTimeout = this.defTimeout;

      config.bufferRows = this.bufferRows;
      config.threads = this.threads;
      config.transRows = this.transRows;

      config.rules = this.rules.map(r => {
        return r.toRowFillerRule();
      });

      return config;
    }
  }
